import { useEffect, useState } from "react";
import {
  api,
  type CycleResponse,
  type PositionResponse,
  type StopMovementResponse,
} from "../api/client";

/**
 * Read-only detail fetcher for a single cycle, backing the Dashboard
 * "active cycle cockpit" and the History "cycle detail drawer".
 *
 * <p>Performs three parallel GETs:
 * <ul>
 *   <li>{@code GET /api/cycles/{id}}              — cycle metadata</li>
 *   <li>{@code GET /api/cycles/{id}/positions}    — open + closed positions</li>
 *   <li>{@code GET /api/cycles/{id}/stop-movements} — accepted + rejected stop moves</li>
 * </ul>
 *
 * <p>Partial-un-stubbing (B5b): the hook exposes three orthogonal
 * flags so the UI can render the right state for each tile —
 * {@code loading}, {@code error}, and the data. Each slot is null
 * until its endpoint has responded successfully.
 */
export interface CycleDetail {
  cycle: CycleResponse | null;
  positions: PositionResponse[] | null;
  stopMovements: StopMovementResponse[] | null;
  loading: boolean;
  error: boolean;
}

export function useCycleDetail(cycleId: number | null, pollMs = 0): CycleDetail {
  const [cycle, setCycle] = useState<CycleResponse | null>(null);
  const [positions, setPositions] = useState<PositionResponse[] | null>(null);
  const [stopMovements, setStopMovements] = useState<StopMovementResponse[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (cycleId == null) {
      setCycle(null);
      setPositions(null);
      setStopMovements(null);
      setLoading(false);
      setError(false);
      return;
    }
    let cancelled = false;
    const tick = async () => {
      setLoading(true);
      try {
        const [c, ps, sms] = await Promise.all([
          api.cycleById(cycleId),
          api.cyclePositions(cycleId),
          api.cycleStopMovements(cycleId),
        ]);
        if (cancelled) return;
        setCycle(c);
        setPositions(ps);
        setStopMovements(sms);
        setError(false);
        setLoading(false);
      } catch {
        if (cancelled) return;
        // Drop cached values so the UI cannot pretend a stale snapshot
        // is current.
        setCycle(null);
        setPositions(null);
        setStopMovements(null);
        setError(true);
        setLoading(false);
      }
    };
    void tick();
    if (pollMs > 0) {
      const id = setInterval(tick, pollMs);
      return () => {
        cancelled = true;
        clearInterval(id);
      };
    }
    return () => {
      cancelled = true;
    };
  }, [cycleId, pollMs]);

  return { cycle, positions, stopMovements, loading, error };
}
